import { useState, useEffect, useCallback } from 'react';
import { useSignalR } from '../context/SignalRContext';
import type { LeaderboardResponse } from '../services/leaderboardService';
import { fetchLeaderboard } from '../services/leaderboardService';
import type { HubConnection } from '@microsoft/signalr';

export const useLeaderboard = () => {
  const { connection } = useSignalR();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (!connection) return;
      const data = await fetchLeaderboard(connection);
      setLeaderboardData(data);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [connection]);

  useEffect(() => {
    loadLeaderboard();

    // Refresh leaderboard every 5 minutes
    const refreshInterval = setInterval(() => {
      loadLeaderboard();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [loadLeaderboard]);

  return {
    leaderboardData,
    entries: leaderboardData?.entries || [],
    loading,
    error,
    lastRefreshed,
    refresh: loadLeaderboard,
  };
};
